import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import TableBox from '../../../components/TableBox';
import StatusBadge from '../../../components/StatusBadge';
import RowActions from '../../../components/RowActions';
import Button from '../../../components/Button';
import Dropdown from '../../../components/Dropdown';
import LoadingRow from '../../../components/TableBox/Rows/LoadingRow';
import useFetch from '../../../hooks/useFetch';
import usePagination from '../../../hooks/usePagination';
import FeatureFlagService from '../../../services/feature-flag.service';
import ProductService from '../../../services/product.service';
import MasterService from '../../../services/master.service';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes } from '../../../enums';
import useSorting from '../../../hooks/useSorting';
import { sortData } from '../../../utils/sortUtils';

interface FeatureFlagRow {
  id: number;
  flag_code: string;
  flag_name: string;
  product_code: string | null;
  default_state: boolean;
  targeting: string;
  is_killed: boolean;
  status_name: string | null;
  status_colour: string | null;
}

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

interface StatusOption {
  id: number;
  status_name: string;
  colour_code: string;
}

const TARGETING_LABELS: Record<string, string> = {
  all_tenants: 'All Tenants',
  by_plan: 'By Plan',
  by_tenant: 'By Tenant',
};

const FeatureFlagList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'flag_code' });
  const [items, setItems] = useState<FeatureFlagRow[]>([]);
  const [total, setTotal] = useState(0);
  const [productFilter, setProductFilter] = useState('');
  const [targetingFilter, setTargetingFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [matrixMode, setMatrixMode] = useState(false);
  const [matrixProductId, setMatrixProductId] = useState('');
  const [matrixData, setMatrixData] = useState<any>(null);
  const [matrixLoading, setMatrixLoading] = useState(false);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
    MasterService.list({ type: 'featureFlagStatuses', includeDeleted: false, limit: 100 }).then((response: any) => {
      setStatuses(response?.data?.items ?? []);
    });
  }, []);

  const { loading } = useFetch({
    service: FeatureFlagService,
    func: 'list',
    payload: {
      page,
      limit: perPage,
      productId: productFilter ? Number(productFilter) : undefined,
      targeting: targetingFilter || undefined,
      statusId: statusFilter ? Number(statusFilter) : undefined,
    },
    dependency: [page, perPage, productFilter, targetingFilter, statusFilter],
    skip: matrixMode,
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const loadMatrix = useCallback(() => {
    if (!matrixProductId) return;
    setMatrixLoading(true);
    FeatureFlagService.getMatrix(Number(matrixProductId))
      .then((response: any) => setMatrixData(response?.data ?? null))
      .catch(() => setMatrixData(null))
      .finally(() => setMatrixLoading(false));
  }, [matrixProductId]);

  useEffect(() => {
    if (matrixMode) loadMatrix();
  }, [matrixMode, loadMatrix]);

  const headCells: HeadCellType[] = useMemo(
    () => [
      { id: 'flag_code', sortEnabled: true, width: '15%', label: t('featureFlags.list.pageTexts.table.columns.flagCode') },
      { id: 'flag_name', sortEnabled: true, width: '18%', label: t('featureFlags.list.pageTexts.table.columns.flagName') },
      { id: 'product_code', sortEnabled: true, width: '12%', label: t('featureFlags.list.pageTexts.table.columns.product') },
      { id: 'default_state', sortEnabled: true, width: '12%', label: t('featureFlags.list.pageTexts.table.columns.defaultState') },
      { id: 'targeting', sortEnabled: true, width: '13%', label: t('featureFlags.list.pageTexts.table.columns.targeting') },
      { id: 'status', sortEnabled: true, width: '13%', label: t('featureFlags.list.pageTexts.table.columns.status') },
      { id: 'actions', sortEnabled: false, width: '17%', label: t('featureFlags.list.pageTexts.table.columns.actions'), textAlign: 'right' },
    ],
    [t]
  );

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: FeatureFlagRow; className?: string }) => (
      <tr className={className}>
        <td>{item.flag_code}</td>
        <td>{item.flag_name}</td>
        <td>{item.product_code ?? '—'}</td>
        <td>
          <Chip
            size="small"
            label={item.default_state ? t('featureFlags.list.pageTexts.defaultOn') : t('featureFlags.list.pageTexts.defaultOff')}
            color={item.default_state ? 'success' : 'default'}
          />
        </td>
        <td>{TARGETING_LABELS[item.targeting] ?? item.targeting}</td>
        <td>
          <div className="d-flex align-items-center gap-2">
            {item.status_name && item.status_colour && <StatusBadge label={item.status_name} colour={item.status_colour} />}
            {item.is_killed && (
              <Tooltip title={t('featureFlags.list.pageTexts.killedTooltip')}>
                <LockIcon fontSize="small" color="error" />
              </Tooltip>
            )}
          </div>
        </td>
        <td className="text-end">
          <RowActions
            actions={[
              {
                id: 'view',
                label: t('common.view') || 'View',
                icon: <i className="ri-eye-line" />,
                onClick: () => navigate(`${Modules.home}${AuthRoutes.featureFlags}/${item.id}`),
              },
              {
                id: 'edit',
                label: t('common.edit') || 'Edit',
                icon: <i className="ri-pencil-line" />,
                onClick: () => navigate(`${Modules.home}${AuthRoutes.featureFlags}${RouteParamsTypes.edit}/${item.id}`),
              },
            ]}
          />
        </td>
      </tr>
    ),
    [navigate, t]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('featureFlags.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  const renderMatrixCell = (effectiveValue: boolean, isKilled: boolean) => {
    if (isKilled) return <LockIcon fontSize="small" color="error" />;
    return effectiveValue ? <CheckCircleIcon fontSize="small" color="success" /> : <CancelIcon fontSize="small" color="error" />;
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('featureFlags.list.pageTexts.title')}</h4>
        <div className="d-flex align-items-center gap-2">
          <Button
            className={`btn btn-sm ${matrixMode ? 'btn-primary' : 'btn-outline-secondary'}`}
            text={t('featureFlags.list.pageTexts.matrixView')}
            onClick={() => setMatrixMode((v) => !v)}
          />
          <Button
            className="btn btn-primary"
            text={t('featureFlags.list.pageTexts.addNew')}
            onClick={() => navigate(`${Modules.home}${AuthRoutes.featureFlags}${RouteParamsTypes.add}`)}
          />
        </div>
      </div>

      {!matrixMode && (
        <>
          <div className="row mb-4 g-2">
            <div className="col-md-4">
              <Dropdown
                name="productFilter"
                value={productFilter}
                placeholder={t('featureFlags.list.pageTexts.filters.allProducts')}
                options={products.map((p) => ({ id: String(p.id), name: p.product_code }))}
                noSearch
                onChange={(e) => setProductFilter(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Dropdown
                name="targetingFilter"
                value={targetingFilter}
                placeholder={t('featureFlags.list.pageTexts.filters.allTargeting')}
                options={Object.entries(TARGETING_LABELS).map(([value, label]) => ({ id: value, name: label }))}
                noSearch
                onChange={(e) => setTargetingFilter(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Dropdown
                name="statusFilter"
                value={statusFilter}
                placeholder={t('featureFlags.list.pageTexts.filters.allStatuses')}
                options={statuses.map((s) => ({ id: String(s.id), name: s.status_name }))}
                noSearch
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>
          </div>

          <TableBox tableValues={values} />
        </>
      )}

      {matrixMode && (
        <>
          <div className="row mb-4">
            <div className="col-md-4">
              <Dropdown
                name="matrixProductId"
                value={matrixProductId}
                placeholder={t('featureFlags.list.pageTexts.filters.selectProductForMatrix')}
                options={products.map((p) => ({ id: String(p.id), name: `${p.product_code} — ${p.product_name}` }))}
                noSearch
                onChange={(e) => setMatrixProductId(e.target.value)}
              />
            </div>
          </div>

          {matrixLoading ? (
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <tbody>
                  <LoadingRow colSpan={6} />
                </tbody>
              </table>
            </div>
          ) : matrixData && matrixData.flags.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>{t('featureFlags.list.pageTexts.table.columns.tenant')}</th>
                    {matrixData.flags.map((flag: any) => (
                      <th key={flag.id} className="text-center">
                        {flag.flagCode}
                        {flag.isKilled && (
                          <Tooltip title={t('featureFlags.list.pageTexts.killedTooltip')}>
                            <LockIcon fontSize="small" color="error" className="ms-1" />
                          </Tooltip>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixData.tenants.map((tenant: any) => (
                    <tr key={tenant.id}>
                      <td>{tenant.name}</td>
                      {matrixData.flags.map((flag: any) => {
                        const cell = matrixData.matrix.find((m: any) => m.flagId === flag.id && m.tenantId === tenant.id);
                        return (
                          <td key={flag.id} className="text-center">
                            {cell ? renderMatrixCell(cell.effectiveValue, flag.isKilled) : <RemoveCircleOutlineIcon fontSize="small" color="disabled" />}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!matrixLoading && matrixData && matrixData.flags.length === 0 && (
            <div className="text-muted">{t('featureFlags.list.pageTexts.table.noDataFoundMessage')}</div>
          )}
        </>
      )}
    </>
  );
};

export default FeatureFlagList;
